import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import VisitorChatbot from './VisitorChatbot';
import { useAuth } from '../contexts/AuthContext';
import UserAvatar from './UserAvatar';

/* ─── Design tokens ────────────────────────────────────────────────── */
const C = {
  bg:       '#010427',
  bg2:      '#05072a',
  bg3:      '#0a0e35',
  card:     'rgba(255,255,255,0.04)',
  border:   'rgba(255,255,255,0.08)',
  lime:     '#bef264',
  limeD:    '#a3e635',
  cyan:     '#67e8f9',
  white:    '#ffffff',
  gray1:    '#e2e8f0',
  gray2:    '#94a3b8',
  gray3:    '#475569',
  gray4:    '#1e293b',
};

const T = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };

/* ─── Reusable micro-components ────────────────────────────────────── */
const Tag = ({ children, color = C.lime }: { children: React.ReactNode; color?: string }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    borderRadius: 20, padding: '4px 14px', fontSize: 14,
    fontWeight: 700, color, letterSpacing: '0.08em', textTransform: 'uppercase',
  }}>{children}</span>
);

const Pill = ({ children }: { children: React.ReactNode }) => (
  <span style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '4px 10px', fontSize: 12,
    fontWeight: 600, color: C.gray2,
  }}>{children}</span>
);

const Check = ({ color = C.lime }: { color?: string }) => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="8.5" cy="8.5" r="8.5" fill={color} fillOpacity="0.15"/>
    <path d="M5 8.5l2.5 2.5L12 5.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7h9M8 3.5L11.5 7 8 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── Main component ───────────────────────────────────────────────── */
const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function LandingPage() {
  const [activeFeature, setActiveFeature] = useState(0);
  const navigate = useNavigate();
  const { user, userDomain } = useAuth();
  const isLoggedIn = !!user;
  const isSystemAdmin = userDomain === 'admin' || !(user as any)?.company_id;
  const dashboardPath = isSystemAdmin ? '/admin/dashboard' : '/company/dashboard';
  const settingsPath = isSystemAdmin ? '/admin/system-settings' : '/company/settings';
  const billingPath = isSystemAdmin ? '/admin/billing' : '/company/billing';
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<string | null>(() => localStorage.getItem('cookie_consent'));



  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const features = [
    {
      tab: 'ESG Integration',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
      desc: 'Unified ESG and emissions tracking with compliance',
      headline: 'Scope 1/2/3 emissions in your compliance workflow.',
      body: 'Track Scope 1, 2, and 3 emissions alongside your compliance frameworks. Set reduction targets, manage carbon offsets, and generate TCFD/CSRD-aligned reports — in the same platform your auditors already use.',
      points: ['Scope 1/2/3 tracking', 'Reduction target setting', 'Carbon offset credits', 'TCFD & CSRD reporting'],
    },
    {
      tab: 'AI Compliance Assistant',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44L4.5 18.5A2.5 2.5 0 0 1 2 16V8a2.5 2.5 0 0 1 2.5-2.5L7 5.5"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44l2.54-1.06A2.5 2.5 0 0 0 22 16V8a2.5 2.5 0 0 0-2.5-2.5L17 5.5"/><circle cx="12" cy="9" r="1"/><circle cx="12" cy="15" r="1"/></svg>,
      desc: 'Proactive AI insights on live compliance data',
      headline: 'AI that understands your compliance role and data.',
      body: 'The only compliance platform with a native AI assistant that queries your live compliance data. It understands your role, evaluates risks proactively, and surfaces insights before audits surface problems.',
      points: ['Persona-aware analysis', 'Proactive risk evaluation', '40+ automated AI tasks', 'Natural language queries'],
    },
    {
      tab: 'Multi-Framework Mapping',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4"/><path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1L3 4c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1"/><path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"/><path d="M7 8h10"/><path d="M7 16h10"/></svg>,
      desc: 'Map controls across ISO 27001, SOC 2, GDPR simultaneously',
      headline: 'One control satisfies multiple frameworks automatically.',
      body: 'Utilize comprehensive frameworks with nested scopes, sections, and requirements. Map a single control to ISO 27001, SOC 2, and NIST simultaneously — do the work once, satisfy every framework automatically.',
      points: ['Multi-framework control reuse', 'Nested hierarchy builder', 'Standard mapping library', 'Version-controlled templates'],
    },
    {
      tab: 'Evidence Automation',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg>,
      desc: 'Automated evidence collection across your entire organization',
      headline: 'Evidence collected automatically, CAPA generated instantly.',
      body: 'Assign compliance pages to team members, track evidence status in real time, and let the platform auto-generate CAPA items when evidence is rejected — closing the loop without manual chasing.',
      points: ['Task-level page assignments', 'Evidence expiry management', 'CAPA auto-generation', 'Bulk evidence upload & OCR'],
    },
    {
      tab: 'Audit Workflows',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
      desc: 'Streamline internal audits and external certifications',
      headline: 'Blind audits, instant certificates, one-click auditor packs.',
      body: 'Separate internal and external auditor roles with identity-blind audit trails. Issue certificates, schedule surveillance audits, and export auditor packs as a single PDF — all from one platform.',
      points: ['Blind audit mechanism', 'Non-conformity tracking', 'Certificate issuance', 'One-click auditor pack PDF'],
    },
    {
      tab: 'Risk Management',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/><path d="M13 13l6 6"/></svg>,
      desc: 'Live risk scoring linked to compliance controls',
      headline: '5x5 risk heatmap with automatic compliance linkage.',
      body: 'Log, assess, and monitor risks on a 5x5 likelihood/impact heatmap. Link risks directly to controls and compliance requirements so your risk posture updates automatically as evidence changes.',
      points: ['5x5 heatmap visualisation', 'Risk-to-control linkage', 'Real-time scoring updates', 'Executive risk reports'],
    },
  ];

  const lifecycle = [
    { n: '01', label: 'Define', desc: 'Frameworks structured by experts', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg> },
    { n: '02', label: 'Assign', desc: 'Requirements mapped to teams', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
    { n: '03', label: 'Monitor', desc: 'Track compliance and risk in real time', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
    { n: '04', label: 'Audit', desc: 'Run internal and external audit workflows', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20,6 9,17 4,12"/></svg> },
    { n: '05', label: 'Certify', desc: 'Issue certifications and maintain readiness', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21,13.89 7,23 12,20 17,23 15.79,13.88"/></svg> },
    { n: '06', label: 'Improve', desc: 'Continuous compliance and CAPA cycles', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23,4 23,10 17,10"/><path d="M20.49,9A9,9,0,1,1,5.64,5.64L23,10"/></svg> },
  ];

  const standards = [
    'ISO 27001', 'SOC 2 Type II', 'GDPR', 'NIST CSF',
    'ISO 14001', 'HIPAA', 'PCI-DSS', 'TCFD', 'CSRD', 'ISO 9001',
  ];

  const stats = [
    { value: '50+', label: 'Frameworks supported', sub: 'out of the box' },
    { value: 'One-to-many', label: 'Control mapping', sub: 'automated framework linkage' },
    { value: 'Expert-led', label: 'Setup process', sub: 'audit-ready from day one' },
  ];

  return (
    <div style={{ ...T, backgroundColor: '#ffffff', color: '#1e293b', overflowX: 'hidden', minHeight: '100vh' }}>

      {/* ── NAVBAR ──────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        backgroundColor: scrolled || menuOpen ? 'rgba(11, 33, 42, 1)' : 'rgba(3,7,18,0.5)',
        backdropFilter: 'blur(12px)',
        borderBottom: scrolled || menuOpen ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.05)',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 66 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 44 }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: C.white }}>
              <svg width="34" height="34" viewBox="0 0 512 512" fill="none">
                <defs>
                  <linearGradient id="nbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                  <linearGradient id="nlf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
                </defs>
                <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#nbg)" strokeWidth="28" strokeLinecap="round"/>
                <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#nbg)" strokeWidth="28" strokeLinecap="round"/>
                <circle cx="256" cy="180" r="38" fill="url(#nbg)"/>
                <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#nbg)"/>
                <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#nlf)"/>
                <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#nlf)"/>
              </svg>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, color: '#22c55e' }}>EIS</span>
            </Link>
            <div className="nav-links" style={{ display: 'flex', gap: 28, fontSize: 14, color: C.gray2 }}>
              {[
                { label: 'Platform',   action: () => scrollTo('platform') },
                { label: 'Solutions',  action: () => scrollTo('lifecycle') },
                { label: 'Frameworks', action: () => scrollTo('frameworks') },
                { label: 'Emissions',  action: () => scrollTo('esg') },
                { label: 'Contact',    action: () => navigate('/contact') },
              ].map(({ label, action }) => (
                <button key={label} onClick={action}
                  style={{ background: 'none', border: 'none', color: C.gray2, cursor: 'pointer', fontSize: 14, padding: 0, transition: 'color 0.2s' }}
                  onMouseOver={e => (e.currentTarget.style.color = C.white)}
                  onMouseOut={e => (e.currentTarget.style.color = C.gray2)}>{label}</button>
              ))}
            </div>
          </div>
          {/* Desktop actions */}
          <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isLoggedIn ? (
              <>
                {/* User profile avatar with dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setProfileOpen(prev => !prev)}
                    style={{ background: 'none', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '50%', cursor: 'pointer', padding: 0, transition: 'border-color 0.2s' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = C.lime)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')}
                  >
                    <UserAvatar user={user} size={36} className="bg-sky-600" />
                  </button>
                  {profileOpen && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 220, backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 200, overflow: 'hidden', textAlign: 'left' }}>
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{(user as any)?.first_name} {(user as any)?.last_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{isSystemAdmin ? 'System Admin' : 'Company Admin'}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{(user as any)?.email}</div>
                      </div>
                      <div style={{ padding: 6 }}>
                        <Link to={dashboardPath} onClick={() => setProfileOpen(false)} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#334155', textDecoration: 'none', borderRadius: 6, transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Dashboard</Link>
                        <Link to={settingsPath} onClick={() => setProfileOpen(false)} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#334155', textDecoration: 'none', borderRadius: 6, transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Settings</Link>
                        <Link to={billingPath} onClick={() => setProfileOpen(false)} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#334155', textDecoration: 'none', borderRadius: 6, transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Billing</Link>
                      </div>
                      <div style={{ padding: 6, borderTop: '1px solid #f1f5f9' }}>
                        <button onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('refreshToken'); window.location.href = '/'; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Sign out</button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" style={{ color: C.gray2, textDecoration: 'none', fontSize: 14, padding: '8px 4px' }}>Sign in</Link>
                <Link to="/contact" style={{ background: 'none', border: `1px solid ${C.border}`, color: C.white, borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 600, textDecoration: 'none', display: 'inline-block' }}>View demo</Link>
                <Link to="/register" style={{ backgroundColor: C.lime, color: '#000', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>Get started</Link>
              </>
            )}
            <button onClick={() => setMenuOpen(o => !o)} style={{ background: 'none', border: 'none', color: C.white, cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, transition: 'background-color 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              {menuOpen ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              )}
            </button>
          </div>
          {/* Mobile: CTA + Hamburger */}
          <div className="nav-mobile" style={{ display: 'none', alignItems: 'center', gap: 10 }}>
            <Link to="/register" style={{ backgroundColor: C.lime, color: '#000', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>Get started</Link>
            <button onClick={() => setMenuOpen(o => !o)} style={{ background: 'none', border: 'none', color: C.white, cursor: 'pointer', padding: 6, display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center', justifyContent: 'center' }}>
              {menuOpen ? (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M5 5l12 12M17 5L5 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000,
                display: 'block'
              }}
              onClick={() => setMenuOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              exit={{ x: 320 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: 320,
                height: '100vh',
                backgroundColor: 'rgb(11 33 42)',
                borderLeft: `1px solid ${C.border}`,
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                padding: '24px',
                overflowY: 'auto'
              }}
            >
              {/* Header with Logo and Close */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <svg width="40" height="40" viewBox="0 0 512 512" fill="none">
                    <defs>
                      <linearGradient id="sbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                      <linearGradient id="slf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
                    </defs>
                    <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#sbg)" strokeWidth="28" strokeLinecap="round"/>
                    <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#sbg)" strokeWidth="28" strokeLinecap="round"/>
                    <circle cx="256" cy="180" r="38" fill="url(#sbg)"/>
                    <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#sbg)"/>
                    <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#slf)"/>
                    <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#slf)"/>
                  </svg>
                  <div>
                    <div style={{ color: C.white, fontSize: 16, fontWeight: 700 }}>EIS Tech</div>
                    <div style={{ color: C.gray2, fontSize: 12 }}>Enterprise Suite</div>
                  </div>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: C.white,
                    cursor: 'pointer',
                    padding: 8,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label="Close menu"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Enterprise Status Bar */}
              <div style={{
                backgroundColor: '#051014',
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 16,
                marginBottom: 24
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{
                    width: 8,
                    height: 8,
                    backgroundColor: C.lime,
                    borderRadius: '50%',
                    animation: 'pulse 2s infinite'
                  }}></div>
                  <span style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>System Status: Active</span>
                </div>
                <div style={{ color: C.gray2, fontSize: 12 }}>
                  All compliance modules operational
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ marginBottom: 32 }}>
                <h3 style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quick Actions
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button style={{
                    backgroundColor: '#0f172a',
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: 12,
                    color: C.gray1,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1e293b';
                    e.currentTarget.style.borderColor = C.lime;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f172a';
                    e.currentTarget.style.borderColor = C.border;
                  }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Compliance Check
                  </button>
                  <button style={{
                    backgroundColor: '#0f172a',
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: 12,
                    color: C.gray1,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1e293b';
                    e.currentTarget.style.borderColor = C.cyan;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f172a';
                    e.currentTarget.style.borderColor = C.border;
                  }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    AI Assistant
                  </button>
                </div>
              </div>

              {/* Navigation */}
              <div style={{ flex: 1 }}>
                <h3 style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Navigation
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    { label: 'Platform Overview', action: () => { scrollTo('platform'); setMenuOpen(false); } },
                    { label: 'Compliance Solutions', action: () => { scrollTo('lifecycle'); setMenuOpen(false); } },
                    { label: 'Framework Library', action: () => { scrollTo('frameworks'); setMenuOpen(false); } },
                    { label: 'ESG Reporting', action: () => { scrollTo('esg'); setMenuOpen(false); } },
                    { label: 'Enterprise Support', action: () => { navigate('/contact'); setMenuOpen(false); } },
                  ].map(({ label, action }) => (
                    <button
                      key={label}
                      onClick={action}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: C.gray1,
                        cursor: 'pointer',
                        fontSize: 16,
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0f172a';
                        e.currentTarget.style.color = C.white;
                        e.currentTarget.style.paddingLeft = '20px';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = C.gray1;
                        e.currentTarget.style.paddingLeft = '16px';
                      }}
                    >
                      <div style={{
                        width: 4,
                        height: 4,
                        backgroundColor: C.lime,
                        borderRadius: '50%',
                        flexShrink: 0,
                        transition: 'all 0.2s ease'
                      }}></div>
                      <span>{label}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{
                          marginLeft: 'auto',
                          opacity: 0.5,
                          transition: 'opacity 0.2s ease'
                        }}
                      >
                        <path d="M4.5 9L7.5 6L4.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              {/* Enterprise Footer */}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24, marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <div style={{
                    width: 6,
                    height: 6,
                    backgroundColor: C.lime,
                    borderRadius: '50%'
                  }}></div>
                  <span style={{ color: C.gray2, fontSize: 12, fontWeight: 600 }}>SECURE ENTERPRISE ACCESS</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      textAlign: 'center',
                      color: C.white,
                      textDecoration: 'none',
                      fontSize: 14,
                      padding: '12px',
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                      e.currentTarget.style.borderColor = C.lime;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = C.border;
                    }}
                  >
                    Enterprise Sign In
                  </Link>
                  <Link
                    to="/contact"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      textAlign: 'center',
                      backgroundColor: C.lime,
                      color: '#000',
                      textDecoration: 'none',
                      fontSize: 14,
                      padding: '12px',
                      borderRadius: 8,
                      fontWeight: 700,
                      transition: 'all 0.2s ease',
                      boxShadow: `0 4px 12px ${C.lime}40`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#a3e635';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 6px 16px ${C.lime}60`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = C.lime;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 4px 12px ${C.lime}40`;
                    }}
                  >
                    Schedule Enterprise Demo
                  </Link>
                </div>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: C.gray3, fontSize: 10, fontWeight: 600 }}>EIS TECH v2.4.1</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{
                        width: 4,
                        height: 4,
                        backgroundColor: C.lime,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        width: 4,
                        height: 4,
                        backgroundColor: C.cyan,
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        width: 4,
                        height: 4,
                        backgroundColor: C.gray3,
                        borderRadius: '50%'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── HERO ────────────────────────────────────────────────────── */}
      <section id="hero" className="hero-section" style={{ position: 'relative', overflow: 'hidden', padding: '200px 28px 120px', backgroundColor: '#030712' }}>
        {/* Background glow */}
        <div style={{ position: 'absolute', top: -200, left: '0%', width: 900, height: 600, background: `radial-gradient(ellipse at center, ${C.lime}18 0%, transparent 65%)`, pointerEvents: 'none' }}/>

        <div style={{ maxWidth: 1420, margin: '0 auto', position: 'relative' }}>
          {/* Background glow */}
          <div style={{ position: 'absolute', top: 0, right: 0, width: 500, height: 500, backgroundColor: 'rgba(34,197,94,0.1)', filter: 'blur(120px)', borderRadius: '50%', pointerEvents: 'none' }}/>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center', position: 'relative', zIndex: 1 }}>
            {/* Left column */}
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '8px 16px', marginBottom: 32 }}>
                <span style={{ width: 8, height: 8, backgroundColor: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite' }}/>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#d1d5db', textTransform: 'uppercase', letterSpacing: '0.1em' }}>EIS TECH</span>
              </div>
              <h1 style={{
                fontSize: 'clamp(28px, 3vw, 46px)',
                fontWeight: 300, lineHeight: 1.15, letterSpacing: '-0.01em',
                marginBottom: 18,
                fontFamily: "'Georgia', 'Times New Roman', serif",
                color: '#ffffff'
              }}>
                The AI-native compliance platform<br/>
                <span style={{ color: '#22c55e', fontStyle: 'italic' }}>
                  for modern enterprises
                </span>
              </h1>
              <p style={{ color: '#d1d5db', fontSize: 15, lineHeight: 1.7, maxWidth: 600, marginBottom: 32 }}>
                Streamline evidence collection, manage risk, pass audits, and track ESG — all in one platform designed to support any compliance framework your organisation runs.
              </p>
              <div style={{ display: 'flex', gap: 14, marginBottom: 40 }}>
                <Link to="/contact" style={{ backgroundColor: C.lime, color: '#000', borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>
                  Request a demo
                </Link>
                <Link to="/contact" style={{ background: C.card, border: `1px solid ${C.border}`, color: C.white, borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${C.lime}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `8px solid ${C.lime}`, marginLeft: 2 }}/>
                  </span>
                  Talk to sales
                </Link>
              </div>
              {/* Trust strip */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                {[
                  { icon: <Check color={C.lime} />, text: 'ISO 27001-ready workflows' },
                  { icon: <Check color={C.lime} />, text: 'UK GDPR-aligned workflows' },
                  { icon: <Check color={C.lime} />, text: 'UK-hosted infrastructure' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.gray3 }}>
                    {item.icon}
                    <span>{item.text}</span>

                  </div>
                ))}
              </div>
            </div>

            {/* Right column — system illustration */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Animated background elements */}
              <div style={{
                position: 'absolute', top: '20%', left: '10%', width: '20px', height: '20px',
                backgroundColor: '#22c55e', borderRadius: '50%', opacity: 0.3,
                animation: 'float 8s ease-in-out infinite'
              }}/>
              <div style={{
                position: 'absolute', top: '60%', right: '15%', width: '15px', height: '15px',
                backgroundColor: '#22c55e', borderRadius: '50%', opacity: 0.4,
                animation: 'float 6s ease-in-out infinite reverse'
              }}/>
              <div style={{
                position: 'absolute', bottom: '30%', left: '20%', width: '25px', height: '25px',
                backgroundColor: '#22c55e', borderRadius: '50%', opacity: 0.2,
                animation: 'float 10s ease-in-out infinite'
              }}/>
              <div style={{
                position: 'absolute', top: '40%', right: '5%', width: '12px', height: '12px',
                backgroundColor: '#22c55e', borderRadius: '50%', opacity: 0.5,
                animation: 'float 7s ease-in-out infinite reverse'
              }}/>
              <style>{`
                @keyframes float {
                  0%, 100% { transform: translateY(0px) rotate(0deg); }
                  50% { transform: translateY(-20px) rotate(180deg); }
                }
              `}</style>
              <svg width="500" height="400" viewBox="0 0 500 400" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: '100%', height: 'auto', position: 'relative', zIndex: 1 }}>
                <defs>
                  <linearGradient id="eisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#16a34a" stopOpacity="0.1"/>
                  </linearGradient>
                  <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0f172a"/>
                    <stop offset="100%" stopColor="#1e293b"/>
                  </linearGradient>
                </defs>

                {/* Background glow */}
                <circle cx="250" cy="200" r="150" fill="url(#eisGradient)" opacity="0.3">
                  <animate attributeName="r" values="150;160;150" dur="3s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.3;0.2;0.3" dur="3s" repeatCount="indefinite"/>
                </circle>

                {/* Central compliance hub */}
                <circle cx="250" cy="200" r="50" fill="url(#eisGradient)" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="250" cy="200" r="25" fill="#22c55e" opacity="0.9"/>
                <text x="250" y="210" textAnchor="middle" fontSize="14" fontWeight="800" fill="#ffffff">EIS</text>

                {/* Pulsing ring */}
                <circle cx="250" cy="200" r="50" fill="none" stroke="#bef264" strokeWidth="2" opacity="0.6">
                  <animate attributeName="r" values="50;70" dur="2s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.6;0" dur="2s" repeatCount="indefinite"/>
                </circle>

                {/* Framework nodes with gradients */}
                <circle cx="120" cy="100" r="30" fill="url(#nodeGradient)" stroke="#22c55e" strokeWidth="2"/>
                <text x="120" y="107" textAnchor="middle" fontSize="11" fill="#ffffff" fontWeight="600">ISO</text>

                <circle cx="380" cy="100" r="30" fill="url(#nodeGradient)" stroke="#22c55e" strokeWidth="2"/>
                <text x="380" y="107" textAnchor="middle" fontSize="11" fill="#ffffff" fontWeight="600">SOC2</text>

                <circle cx="120" cy="300" r="30" fill="url(#nodeGradient)" stroke="#22c55e" strokeWidth="2"/>
                <text x="120" y="307" textAnchor="middle" fontSize="11" fill="#ffffff" fontWeight="600">GDPR</text>

                <circle cx="380" cy="300" r="30" fill="url(#nodeGradient)" stroke="#22c55e" strokeWidth="2"/>
                <text x="380" y="307" textAnchor="middle" fontSize="11" fill="#ffffff" fontWeight="600">NIST</text>

                {/* Curved connecting paths */}
                <path d="M200 175 Q250 150 300 175" stroke="#22c55e" strokeWidth="2" fill="none" opacity="0.7" strokeDasharray="5,5"/>
                <path d="M200 225 Q250 250 300 225" stroke="#22c55e" strokeWidth="2" fill="none" opacity="0.7" strokeDasharray="5,5"/>

                {/* AI brain element */}
                <g transform="translate(230,160)">
                  <circle cx="20" cy="20" r="18" fill="#22c55e" opacity="0.9"/>
                  <path d="M12 14 Q20 8 28 14 Q28 18 20 22 Q12 18 12 14 Z" fill="#ffffff" opacity="0.8"/>
                  <circle cx="16" cy="12" r="2" fill="#22c55e"/>
                  <circle cx="24" cy="12" r="2" fill="#22c55e"/>
                </g>
                <text x="250" y="190" textAnchor="middle" fontSize="9" fill="#22c55e" fontWeight="700">AI BRAIN</text>

                {/* Data flow particles */}
                <circle cx="180" cy="140" r="3" fill="#22c55e" opacity="0.8"/>
                <circle cx="320" cy="140" r="3" fill="#22c55e" opacity="0.8"/>
                <circle cx="180" cy="260" r="3" fill="#22c55e" opacity="0.8"/>
                <circle cx="320" cy="260" r="3" fill="#22c55e" opacity="0.8"/>

                {/* Success indicators */}
                <g transform="translate(100,85)">
                  <circle cx="10" cy="10" r="8" fill="#22c55e" opacity="0.2"/>
                  <path d="M6 10 L9 13 L14 8" stroke="#22c55e" strokeWidth="2" fill="none"/>
                </g>
                <g transform="translate(360,85)">
                  <circle cx="10" cy="10" r="8" fill="#22c55e" opacity="0.2"/>
                  <path d="M6 10 L9 13 L14 8" stroke="#22c55e" strokeWidth="2" fill="none"/>
                </g>
                <g transform="translate(100,285)">
                  <circle cx="10" cy="10" r="8" fill="#22c55e" opacity="0.2"/>
                  <path d="M6 10 L9 13 L14 8" stroke="#22c55e" strokeWidth="2" fill="none"/>
                </g>
                <g transform="translate(360,285)">
                  <circle cx="10" cy="10" r="8" fill="#22c55e" opacity="0.2"/>
                  <path d="M6 10 L9 13 L14 8" stroke="#22c55e" strokeWidth="2" fill="none"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXPERT-BUILT SECTION ─────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.3\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1\'/%3E%3C/g%3E%3C/svg%3E")', position: 'relative' }}>
        {/* Background Shapes */}
        <div style={{ position: 'absolute', top: '15%', left: '5%', opacity: 0.05, zIndex: 0 }}>
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="50" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
        <div style={{ position: 'absolute', top: '60%', right: '8%', opacity: 0.05, zIndex: 0 }}>
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="10" width="60" height="60" stroke="#1e293b" strokeWidth="1" rx="8"/>
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '20%', left: '15%', opacity: 0.05, zIndex: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="50,10 90,90 10,90" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '80px 28px', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Tag>Unfair Advantage</Tag>
          <h2 style={{ fontSize: 'clamp(28px,3vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', margin: '20px 0 16px', lineHeight: 1.15, color: '#1e293b' }}>
            You don't start from scratch.<br/>We set it up for you.
          </h2>
        </div>
        <div className="comparison-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 32,
          maxWidth: 900,
          margin: '0 auto'
        }}>
          <div style={{ backgroundColor: '#f8fafc', borderRadius: 16, padding: 'clamp(20px, 5vw, 32px)', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Most Compliance Platforms</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: '#475569' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                Configure frameworks yourself
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: '#475569' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                Map controls manually
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: '#475569' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                Structure everything from scratch
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: '#475569' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
                Months to get audit-ready
              </li>
            </ul>
          </div>
          <div style={{ background: `linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)`, borderRadius: 16, padding: 'clamp(20px, 5vw, 32px)', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 14, color: '#16a34a', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Framework Mapping UI</div>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12, color: '#475569', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>ISO 27001 A.9.1.1</div>
              <div>Access Control Policy</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                <span style={{ background: '#16a34a', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>SOC 2</span>
                <span style={{ background: '#0891b2', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>GDPR</span>
                <span style={{ background: '#dc2626', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>NIST</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>One control, multiple frameworks — automatically mapped and evidence collected.</div>
          </div>
        </div>
      </div>
      </section>

      {/* ── TRUST BAR + LIFECYCLE ───────────────────────────────────── */}
      <section id="lifecycle" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.2\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")', position: 'relative' }}>
        {/* Background Shapes */}

        <div style={{ position: 'absolute', bottom: '15%', left: '8%', opacity: 0.05, zIndex: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="40" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '28px 28px 0' }}>
          {/* Trust bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20, paddingBottom: 28, borderBottom: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Built for modern compliance teams ready to transform their workflow
            </p>
            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
              {[
                { name: 'AI-Powered', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44L4.5 18.5A2.5 2.5 0 0 1 2 16V8a2.5 2.5 0 0 1 2.5-2.5L7 5.5"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44l2.54-1.06A2.5 2.5 0 0 0 22 16V8a2.5 2.5 0 0 0-2.5-2.5L17 5.5"/><circle cx="12" cy="9" r="1"/><circle cx="12" cy="15" r="1"/></svg> },
                { name: 'Cloud-Native', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg> },
                { name: 'Secure', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg> },
                { name: 'Scalable', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 17 10-10M7 7h10v10"/></svg> }
              ].map(({ name, icon }) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: 8, transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  {icon}
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#475569', letterSpacing: '0.04em' }}>{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lifecycle */}
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '120px 28px 120px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <Tag>Full Lifecycle</Tag>
            <h2 style={{ fontSize: 'clamp(24px,2.5vw,38px)', fontWeight: 700, letterSpacing: '-0.02em', margin: '20px 0 16px', lineHeight: 1.15, color: '#1e293b' }}>
              One platform for every step<br/>of your compliance journey
            </h2>
            <p style={{ color: '#64748b', fontSize: 15, maxWidth: 600, margin: '0 auto', lineHeight: 1.7 }}>
              From framework definition to certificate issuance — every stage is connected, automated, and auditable.
            </p>
          </div>

          <div className="lifecycle-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 32, position: 'relative', maxWidth: 1200, margin: '0 auto' }}>

            {lifecycle.map((step, i) => (
              <div key={i} style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '20px 16px', backgroundColor: '#ffffff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
                  background: i === 0 ? `linear-gradient(135deg, #16a34a, #4ade80)` : `linear-gradient(135deg, #f8fafc, #f1f5f9)`,
                  border: i === 0 ? 'none' : '2px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: i === 0 ? '#ffffff' : '#64748b',
                  transition: 'all 0.3s ease'
                }}>
                  {React.cloneElement(step.icon, { style: { width: 20, height: 20 } })}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{step.n}</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>{step.label}</div>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE TABS ────────────────────────────────────────────── */}
      <section id="platform" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.3\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1\'/%3E%3C/g%3E%3C/svg%3E")' }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <Tag>Platform Capabilities</Tag>
            <h2 style={{ fontSize: 'clamp(28px,3vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '20px 0 0', lineHeight: 1.15, color: '#1e293b' }}>
              Built for real-world<br/>compliance operations
            </h2>
          </div>

          {/* Tab bar */}
          <div
            style={{ marginBottom: 48, position: 'relative' }}
            onMouseEnter={(e) => {
              const container = e.currentTarget.querySelector('.tab-container') as HTMLElement;
              if (container) {
                container.style.overflowX = 'auto';
              }
            }}
            onMouseLeave={(e) => {
              const container = e.currentTarget.querySelector('.tab-container') as HTMLElement;
              if (container) {
                container.style.overflowX = 'hidden';
              }
            }}
          >
            <div className="tab-container" style={{
              display: 'flex', gap: 8, overflowX: 'hidden',
              paddingTop: 4, paddingBottom: 12,
              transition: 'overflow-x 0.2s ease'
            }}>
              <style>{`
                .tab-container::-webkit-scrollbar {
                  height: 4px;
                }
                .tab-container::-webkit-scrollbar-track {
                  background: transparent;
                }
                .tab-container::-webkit-scrollbar-thumb {
                  background: #e2e8f0;
                  border-radius: 2px;
                }
                .tab-container::-webkit-scrollbar-thumb:hover {
                  background: #cbd5e1;
                }
              `}</style>
            {features.map((f, i) => (
              <button key={i} onClick={() => setActiveFeature(i)} style={{
                background: activeFeature === i ? '#f0fdf4' : '#f8fafc',
                cursor: 'pointer', borderRadius: 12,
                padding: '16px 24px', fontSize: 14, fontWeight: 600,
                color: activeFeature === i ? C.lime : '#64748b', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 10,
                transition: 'all 0.3s ease',
                boxShadow: activeFeature === i ? '0 2px 8px rgba(22, 163, 74, 0.1)' : 'none',
                transform: activeFeature === i ? 'translateY(-2px)' : 'translateY(0)',
              }} onMouseEnter={(e) => {
                if (activeFeature !== i) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                }
              }} onMouseLeave={(e) => {
                if (activeFeature !== i) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}>
                <span style={{ color: activeFeature === i ? C.lime : '#64748b', transition: 'color 0.2s' }}>
                  {React.cloneElement(f.icon, { style: { width: 18, height: 18 } })}
                </span>
                {f.tab}
              </button>
            ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="feature-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ color: C.lime }}>
                  {features[activeFeature]?.icon && React.cloneElement(features[activeFeature].icon, { style: { width: 24, height: 24 } })}
                </div>
                <span style={{ fontSize: 14, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {features[activeFeature]?.tab}
                </span>
              </div>
              <h3 style={{ fontSize: 'clamp(24px,2.5vw,36px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.2, marginBottom: 20, color: '#1e293b' }}>
                {features[activeFeature]?.headline || 'Feature Title'}
              </h3>
              <p style={{ color: '#64748b', fontSize: 17, lineHeight: 1.7, marginBottom: 32 }}>
                {features[activeFeature]?.body || 'Feature description'}
              </p>
              <div style={{ backgroundColor: '#f8fafc', borderRadius: 12, padding: 24, marginBottom: 32 }}>
                <div style={{ fontSize: 14, color: '#64748b', fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key Features</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {features[activeFeature]?.points?.map(pt => (
                    <div key={pt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#475569' }}>
                      <div style={{ color: C.lime, flexShrink: 0 }}>
                        <Check />
                      </div>
                      <span>{pt}</span>
                    </div>
                  )) || []}
                </div>
              </div>
              <Link to="/login" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                backgroundColor: C.lime, color: '#ffffff', padding: '12px 24px',
                borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none',
                transition: 'all 0.2s ease'
              }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.3)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                Explore this feature <Arrow />
              </Link>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '1px solid #e2e8f0', borderRadius: 20, overflow: 'hidden',
              aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)', position: 'relative'
            }}>
              <div style={{
                position: 'absolute', top: 20, left: 20,
                backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#64748b'
              }}>
                {features[activeFeature]?.tab}
              </div>
              <div
                role="img"
                aria-label={features[activeFeature]?.tab || 'Feature'}
                style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, #0c1530 0%, #1e293b 100%)',
                  color: '#bef264', fontSize: 22, fontWeight: 700,
                  letterSpacing: 0.5, textAlign: 'center', padding: 24,
                }}
              >
                {features[activeFeature]?.tab || 'Feature'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI SPOTLIGHT ────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'50\' height=\'50\' viewBox=\'0 0 50 50\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.25\'%3E%3Ccircle cx=\'25\' cy=\'25\' r=\'1\'/%3E%3C/g%3E%3C/svg%3E")', padding: '100px 28px', position: 'relative' }}>
        {/* Background Shapes */}
        <div style={{ position: 'absolute', top: '20%', left: '3%', opacity: 0.05, zIndex: 0 }}>
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="60,10 110,60 60,110 10,60" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '25%', right: '5%', opacity: 0.05, zIndex: 0 }}>
          <svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="45" cy="45" r="35" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
          <div className="ai-panel" style={{
            backgroundColor: '#ffffff',
            borderRadius: 24, overflow: 'hidden',
            display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 420,
            maxWidth: 1420, margin: '0 auto', position: 'relative', zIndex: 1,
          }}>
          {/* Left */}
          <div className="ai-text" style={{ padding: '60px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left' }}>
            <Tag color={C.cyan}>AI-Native Intelligence</Tag>
            <h2 style={{ fontSize: 'clamp(24px,2.5vw,38px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, margin: '24px 0 20px', color: '#1e293b' }}>
              AI built directly into<br/>your compliance workflow
            </h2>
            <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.75, marginBottom: 32 }}>
              EIS Tech's AI assistant doesn't just answer questions — it reads your live compliance data, understands your role, and proactively flags risks before your next audit surfaces them.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
              {[
                'Persona-aware analysis — responses tailored to your role',
                '40+ automated compliance AI tasks built in',
                'Proactive risk evaluation, not reactive Q&A',
                'Natural language queries on live compliance data',
              ].map(text => (
                <div key={text} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#475569' }}>
                  <Check /><span>{text}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 36, lineHeight: 1.6 }}>
              AI responses are generated to assist workflows and should be reviewed where necessary.
            </p>
            <Link to="/login" style={{ alignSelf: 'flex-start', backgroundColor: C.cyan, color: '#000', borderRadius: 8, padding: '12px 26px', fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>
              See AI in action
            </Link>
          </div>
          {/* Right — abstract AI visual */}
          <div className="ai-chat" style={{ borderLeft: '1px solid #e2e8f0', background: `linear-gradient(135deg, #f1f5f9, #e2e8f0)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 320, height: 320, borderRadius: '50%', background: `radial-gradient(circle, ${C.cyan}18 0%, transparent 65%)`, pointerEvents: 'none' }}/>
            {/* AI chat bubbles mockup */}
            <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
              {[
                { from: 'user', text: 'Which ISO 27001 controls are at risk before our audit?' },
                { from: 'ai', text: 'I\'ve found 3 controls with expiring evidence in A.9 (Access Control). Clause A.9.4.1 shows evidence last updated 94 days ago — renewal is overdue. Would you like me to create CAPA items?' },
                { from: 'user', text: 'Yes, auto-create the CAPAs and notify the responsible owners.' },
              ].map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.from === 'user' ? 'flex-end' : 'flex-start',
                  background: msg.from === 'user' ? '#ecfeff' : '#f8fafc',
                  border: `1px solid ${msg.from === 'user' ? '#67e8f9' : '#e2e8f0'}`,
                  borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  padding: '12px 16px', maxWidth: '82%', fontSize: 13, lineHeight: 1.55, color: '#475569',
                }}>{msg.text}</div>
              ))}
              <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 6, padding: '10px 16px' }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0891b2', opacity: 0.6 }}/>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MULTI-FRAMEWORK ─────────────────────────────────────────── */}
      <section id="frameworks" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'80\' height=\'80\' viewBox=\'0 0 80 80\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.15\'%3E%3Ccircle cx=\'40\' cy=\'40\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")' }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
          <div className="fw-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <div style={{ textAlign: 'left' }}>
              <Tag>Multi-Framework</Tag>
              <h2 style={{ fontSize: 'clamp(28px,2.5vw,40px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, margin: '24px 0 20px', color: '#1e293b' }}>
                Do the work once.<br/>Map across every framework.
              </h2>
              <p style={{ color: '#64748b', fontSize: 16, lineHeight: 1.75, marginBottom: 32 }}>
                Map a single control to ISO 27001, SOC 2, and GDPR simultaneously. When evidence is collected, it satisfies all mapped requirements at once — eliminating duplicated effort across frameworks.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
                {[
                  'Reusable control library mapped to multiple standards',
                  'Cross-framework gap analysis in one dashboard',
                  'Framework-agnostic — build any custom framework',
                  'Auto-assign requirements to teams by framework',
                ].map(pt => (
                  <div key={pt} style={{ display: 'flex', gap: 10, fontSize: 15, color: '#475569' }}>
                    <Check /><span>{pt}</span>
                  </div>
                ))}
              </div>
              <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: C.lime, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
                Explore framework management <Arrow />
              </Link>
            </div>
            {/* Visual */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {standards.slice(0, 8).map(std => (
                <div key={std} style={{
                  background: '#f8fafc', borderRadius: 12,
                  padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.lime, flexShrink: 0 }}/>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{std}</span>
                </div>
              ))}
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12,
                padding: '18px 20px', gridColumn: 'span 2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontSize: 14, fontWeight: 700, color: C.lime,
              }}>
                + 40 more frameworks supported
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST PORTAL FEATURE ────────────────────────────────────── */}
      <section style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
        <div className="trust-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          {/* Visual */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', inset: -20, background: `radial-gradient(ellipse at center, ${C.lime}15 0%, transparent 70%)`, borderRadius: 40, pointerEvents: 'none' }}/>
            <div style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: `1px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', position: 'relative', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', transition: 'all 0.3s ease', cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}>
              {/* Mock trust portal */}
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, backgroundColor: '#ffffff' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#16a34a', boxShadow: '0 0 8px rgba(22, 163, 74, 0.4)' }}/>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>trust.yourcompany.com</span>
                <div style={{ marginLeft: 'auto', background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', border: '1px solid #86efac', borderRadius: 16, padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a' }}/>
                  LIVE
                </div>
              </div>
              <div style={{ padding: 28 }}>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Active Certifications</div>
                  {[
                    { name: 'ISO 27001:2022', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg> },
                    { name: 'SOC 2 Type II', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg> },
                    { name: 'GDPR Compliant', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg> }
                  ].map((cert, i) => (
                    <div key={cert.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: i < 2 ? `1px solid #e2e8f0` : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {cert.icon}
                        <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{cert.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#16a34a' }}>
                        <Check color="#16a34a"/> Certified
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', border: `1px solid #bbf7d0`, borderRadius: 12, padding: '16px 20px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 12, left: 16, width: 4, height: 4, borderRadius: '50%', backgroundColor: '#16a34a' }}/>
                  <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 4 }}>Last Verified</div>
                  <div style={{ fontSize: 13, color: '#166534' }}>Compliance posture verified 2 hours ago</div>
                </div>
              </div>
            </div>
          </div>
          {/* Text */}
          <div style={{ textAlign: 'left' }}>
            <Tag>Trust Portal</Tag>
            <h2 style={{ fontSize: 'clamp(28px,2.5vw,40px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, margin: '24px 0 20px' }}>
              Show customers your compliance<br/>in real time
            </h2>
            <p style={{ color: C.gray2, fontSize: 16, lineHeight: 1.75, marginBottom: 32 }}>
              EIS Tech generates a public-facing Trust Portal that displays your live certification status — no login required. Share a URL with prospects, customers, or regulators to instantly prove compliance.
            </p>
            {[
              { text: 'Public URL, no login required for viewers', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> },
              { text: 'Updates live as certificates are issued', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg> },
              { text: 'Audit-ready compliance timeline included', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg> }
            ].map(({ text, icon }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: '#475569', marginBottom: 16, padding: '8px 0' }}>
                <div style={{ color: '#16a34a', flexShrink: 0 }}>
                  {icon}
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ESG SECTION ─────────────────────────────────────────────── */}
      <section id="esg" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'70\' height=\'70\' viewBox=\'0 0 70 70\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.2\'%3E%3Ccircle cx=\'35\' cy=\'35\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")' }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <Tag color="#4ade80">ESG & Emissions</Tag>
            <h2 style={{ fontSize: 'clamp(28px,3vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '20px 0 16px', lineHeight: 1.15, color: '#1e293b' }}>
              Compliance and ESG —<br/>unified in one platform
            </h2>
            <p style={{ color: '#64748b', fontSize: 16, maxWidth: 700, margin: '0 auto', lineHeight: 1.7 }}>
              Track Scope 1, 2, and 3 emissions alongside your compliance frameworks. One team, one platform, one audit trail.
            </p>
          </div>
          <div className="esg-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              { title: 'Scope 1/2/3 Tracking', desc: 'Log direct and indirect emissions with automatic GHG Protocol categorisation and calculation.' },
              { title: 'Reduction Targets', desc: 'Set science-based reduction targets and track progress against them with visual dashboards.' },
              { title: 'TCFD & CSRD Reports', desc: 'Generate regulatory-grade ESG reports aligned to TCFD, CSRD, and ISO 14001 automatically.' },
              { title: 'Carbon Offset Credits', desc: 'Manage and track carbon offset credits alongside your emissions data in one ledger.' },
              { title: 'Bulk Calculator', desc: 'Upload bulk emissions data from your supply chain and calculate your entire carbon footprint.' },
              { title: 'Linked to Compliance', desc: 'ESG data sits alongside ISO 14001 and CSRD compliance evidence — audited in the same workflow.' },
            ].map(card => (
              <div key={card.title} style={{ background: '#f8fafc', borderRadius: 16, padding: 28 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 10, color: '#1e293b' }}>{card.title}</h3>
                <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.65 }}>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ───────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ color: C.gray2, fontSize: 16, lineHeight: 1.7, maxWidth: 600, margin: '0 auto' }}>
            Reduce manual compliance effort and accelerate audit readiness with automated workflows and real-time visibility.
          </p>
        </div>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 2, justifyContent: 'center' }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '40px 24px', borderRight: i < stats.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 12, background: `linear-gradient(135deg, ${C.lime}, ${C.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                {s.value}
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 13, color: C.gray3 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECURITY SECTION ──────────────────────────────────────── */}
      <section style={{ maxWidth: 1420, margin: '0 auto', padding: '0 28px 100px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Tag>Security</Tag>
          <h2 style={{ fontSize: 'clamp(24px,2.5vw,38px)', fontWeight: 700, letterSpacing: '-0.02em', margin: '20px 0 16px', lineHeight: 1.15 }}>
            Security and data protection built in
          </h2>
        </div>
        <div className="security-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24 }}>
          {[
            { icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>, title: 'End-to-end encryption', desc: 'In transit and at rest' },
            { icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>, title: 'Role-based access control', desc: 'RBAC with granular permissions' },
            { icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, title: 'Multi-tenant isolation', desc: 'Complete data separation' },
            { icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>, title: 'Backup & recovery', desc: 'Disaster recovery built in' },
            { icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="2"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>, title: 'UK GDPR aligned', desc: 'Designed for UK requirements' },
          ].map((item, i) => (
            <div key={i} style={{ background: '#f8fafc', borderRadius: 16, padding: '32px 24px', textAlign: 'center' }}>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>{item.icon}</div>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>{item.title}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'90\' height=\'90\' viewBox=\'0 0 90 90\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'45\' cy=\'45\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")' }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '100px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <Tag>Customer Stories</Tag>
            <h2 style={{ fontSize: 'clamp(28px,3vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '20px 0 0', lineHeight: 1.15, color: '#1e293b' }}>
              Built for the way<br/>modern compliance teams actually work
            </h2>
            <p style={{ color: '#64748b', fontSize: 17, lineHeight: 1.7, maxWidth: 720, margin: '20px auto 0' }}>
              Every feature on this page exists because compliance teams told us the old way
              — spreadsheets, email threads, and manual mapping — breaks under modern audit
              pressure. EIS-Tech is built to replace all of it.
            </p>
          </div>

          {/* Three capability pillars — honest replacement for fake review/testimonial block */}
          <div className="capability-pillars" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              {
                title: 'One source of truth for every framework',
                body: 'Upload a framework PDF, let the parser structure it, and map controls across every standard you run. When evidence is collected once, it satisfies every mapped requirement in one click.',
                tag: 'Multi-framework',
              },
              {
                title: 'AI that reads your live compliance data',
                body: 'Six persona-scoped AI assistants — not a generic chatbot. Each sees only what their role should see, uses live company context, and only writes with explicit confirmation.',
                tag: 'Persona AI',
              },
              {
                title: 'Full lifecycle under one audit trail',
                body: 'Framework definition, assignment, execution, internal review, external audit, certificate issuance, and CAPA — every stage is connected and logged in the same system of record.',
                tag: 'Lifecycle',
              },
            ].map((p, i) => (
              <div key={i} style={{ background: i === 1 ? `linear-gradient(135deg, #f0fdf4, #ecfeff)` : '#f8fafc', borderRadius: 16, padding: 28, textAlign: 'left' }}>
                <div style={{ marginBottom: 16 }}><Pill>{p.tag}</Pill></div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginBottom: 12, lineHeight: 1.3 }}>{p.title}</h3>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.75, margin: 0 }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23f8fafc\' fill-opacity=\'0.05\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")', padding: '100px 28px', position: 'relative' }}>
        {/* Background Shapes */}
        <div style={{ position: 'absolute', top: '15%', left: '10%', opacity: 0.05, zIndex: 0 }}>
          <svg width="150" height="150" viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="25" width="100" height="100" stroke="#1e293b" strokeWidth="1" rx="15"/>
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '20%', right: '10%', opacity: 0.05, zIndex: 0 }}>
          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="55" cy="55" r="45" stroke="#1e293b" strokeWidth="1"/>
          </svg>
        </div>
        <div className="cta-inner" style={{
          background: '#ffffff',
          borderRadius: 28,
          padding: '72px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          maxWidth: 1420, margin: '0 auto', zIndex: 1
        }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 400, background: `radial-gradient(ellipse, ${C.lime}12 0%, transparent 60%)`, pointerEvents: 'none' }}/>
          <div style={{ position: 'relative' }}>
            <Tag>Get Started Today</Tag>
            <h2 style={{ fontSize: 'clamp(32px,4vw,56px)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.05, margin: '24px 0 20px', color: '#1e293b' }}>
              Get audit-ready faster<br/>with EIS
            </h2>
            <p style={{ color: '#64748b', fontSize: 18, lineHeight: 1.7, maxWidth: 700, margin: '0 auto 44px' }}>
              Compliance teams use EIS-Tech to pass audits faster, reduce risk, and stay ahead of the next regulation — without losing the trail.
            </p>
            <div className="cta-buttons" style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 28 }}>
              <Link to="/contact" style={{ backgroundColor: C.lime, color: '#000', borderRadius: 10, padding: '15px 36px', fontSize: 16, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>
                Request a demo
              </Link>
              <Link to="/contact" style={{ background: 'transparent', border: `1px solid #1e293b`, color: '#1e293b', borderRadius: 10, padding: '15px 36px', fontSize: 16, fontWeight: 600, textDecoration: 'none', display: 'inline-block' }}>
                Talk to sales
              </Link>
            </div>
            <p style={{ fontSize: 13, color: C.gray3 }}>Guided onboarding &nbsp;·&nbsp; UK-hosted &nbsp;·&nbsp; Built for audit-grade evidence</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer style={{ backgroundColor: '#030712', borderTop: `1px solid rgba(255,255,255,0.08)` }}>
        <div style={{ maxWidth: 1420, margin: '0 auto', padding: '72px 28px 40px' }}>
          <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 48, marginBottom: 64 }}>
            {/* Brand */}
            <div style={{ textAlign: 'left', gridRow: 'span 2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <svg width="36" height="36" viewBox="0 0 512 512" fill="none">
                  <defs>
                    <linearGradient id="fbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                    <linearGradient id="flf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
                  </defs>
                  <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#fbg)" strokeWidth="28" strokeLinecap="round"/>
                  <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#fbg)" strokeWidth="28" strokeLinecap="round"/>
                  <circle cx="256" cy="180" r="38" fill="url(#fbg)"/>
                  <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#fbg)"/>
                  <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#flf)"/>
                  <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#flf)"/>
                </svg>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, color: '#ffffff', marginBottom: 2 }}>EIS  </div>
                </div>
              </div>
              <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6, maxWidth: 400, marginBottom: 24 }}>
                The AI-native compliance platform trusted by enterprise teams worldwide. Managing multiple frameworks, risk assessments, and ESG reporting — all in one unified platform.
              </p>

              {/* Frameworks supported by the platform (NOT certifications Yiontech holds) */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Frameworks supported by the platform</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  {[
                    'ISO 27001',
                    'SOC 2',
                    'UK GDPR',
                    'ISO 14001',
                    'NIST CSF',
                    'PCI-DSS',
                  ].map(name => (
                    <div key={name} style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#ffffff', marginBottom: 2 }}>{name}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Supported</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>
                  Framework support means customers can run these standards on the platform. Yiontech Ltd
                  itself does not currently hold accredited certifications against these frameworks.
                </div>
              </div>

              {/* Contact Info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 16, height: 16, color: '#64748b' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <a href="mailto:support@eis-tech.co.uk" style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 13 }}>support@eis-tech.co.uk</a>
                </div>
              </div>
            </div>
            {/* Cols */}
            {([
              {
                sections: [
                  { heading: 'Platform Solutions', links: [
                    { label: 'Compliance Management', desc: 'Unified framework management', to: '/page/compliance-management' },
                    { label: 'Risk Intelligence', desc: 'AI-powered risk assessment', to: '/page/risk-intelligence' },
                    { label: 'Audit Automation', desc: 'Streamlined audit workflows', to: '/page/audit-automation' },
                    { label: 'Trust Portal', desc: 'Public compliance verification', to: '/page/trust-portal' },
                    { label: 'ESG Reporting', desc: 'Comprehensive ESG analytics', to: '/page/esg-reporting' },
                    { label: 'API Integration', desc: 'Enterprise system connectivity', to: '/page/api-integration' }
                  ]}
                ]
              },
              {
                sections: [
                  { heading: 'Compliance Frameworks', links: [
                    { label: 'ISO 27001', desc: 'Information Security', to: '/page/iso-27001' },
                    { label: 'SOC 2', desc: 'Trust Services', to: '/page/soc-2' },
                    { label: 'GDPR', desc: 'Data Protection', to: '/page/gdpr' },
                    { label: 'NIST CSF', desc: 'Cybersecurity', to: '/page/nist-csf' },
                    { label: 'ISO 14001', desc: 'Environmental', to: '/page/iso-14001' },
                    { label: 'PCI-DSS', desc: 'Payment Security', to: '/page/pci-dss' }
                  ]}
                ]
              },
              {
                sections: [
                  { heading: 'Enterprise Services', links: [
                    { label: 'Implementation', desc: 'Full platform setup', to: '/page/implementation' },
                    { label: 'Training', desc: 'Team enablement', to: '/page/training' },
                    { label: 'Consulting', desc: 'Compliance strategy', to: '/page/consulting' },
                    { label: 'Support', desc: 'Business-hours platform support', to: '/page/enterprise-support' },
                    { label: 'Custom Integration', desc: 'Tailored solutions', to: '/page/custom-integration' },
                    { label: 'Compliance Audit', desc: 'Gap analysis & remediation', to: '/page/compliance-audit' }
                  ]}
                ]
              }
            ] as { sections: { heading: string; links: any[] }[] }[]).map((col, index) => (
              <div key={`col-${index}`} style={{ textAlign: 'left' }}>
                {col.sections.map(section => (
                  <div key={section.heading}>
                    <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#ffffff', marginBottom: 20 }}>{section.heading}</h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...(section.heading === 'Resources' ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 } : { display: 'flex', flexDirection: 'column', gap: 12 }) }}>
                      {section.links.map((item, i) => (
                        <li key={item.label || item.name}>
                          <div>
                            <div style={{ marginBottom: 2 }}>
                              <Link to={item.to || '/contact'} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}>{item.label}</Link>
                            </div>
                            {item.desc && <div style={{ fontSize: 11, color: '#64748b' }}>{item.desc}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* Newsletter and Social for Resources section */}
                    {section.heading === 'Resources' && (
                      <div style={{ marginTop: 24, display: 'flex', gap: 24 }}>
                        <div style={{ flex: 1, padding: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                          <div style={{ fontSize: 12, color: '#ffffff', fontWeight: 600, marginBottom: 8 }}>Stay Updated</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>Get the latest compliance insights and platform updates.</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="email"
                              placeholder="Enter email"
                              style={{
                                flex: 1,
                                padding: '6px 12px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 4,
                                color: '#ffffff',
                                fontSize: 12,
                                outline: 'none'
                              }}
                            />
                            <button style={{
                              padding: '6px 12px',
                              background: '#22c55e',
                              border: 'none',
                              borderRadius: 4,
                              color: '#ffffff',
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}>Subscribe</button>
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>Follow Us</div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            {[
                              { name: 'LinkedIn', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                              { name: 'Twitter', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg> },
                              { name: 'YouTube', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> }
                            ].map(social => (
                              <a key={social.name} href="#" style={{ color: '#64748b', textDecoration: 'none' }} title={social.name}>
                                {social.icon}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Resources below Platform Solutions */}
            <div style={{ gridColumn: 2, gridRow: 2, textAlign: 'left' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#ffffff', marginBottom: 20 }}>Resources</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Documentation', to: '/page/documentation' },
                  { label: 'API Reference', to: '/page/api-reference' },
                  { label: 'Compliance Guides', to: '/page/compliance-guides' },
                  { label: 'Case Studies', to: '/page/case-studies' },
                  { label: 'Webinars', to: '/page/webinars' },
                  { label: 'Community Forum', to: '/page/community-forum' }
                ].map((item, i) => (
                  <li key={item.label}>
                    <Link to={item.to} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter in column 3 */}
            <div style={{ gridColumn: 3, gridRow: 2, textAlign: 'left' }}>
              <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#ffffff', fontWeight: 600, marginBottom: 8 }}>Stay Updated</div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>Get the latest compliance insights and platform updates.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    placeholder="Enter email"
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 4,
                      color: '#ffffff',
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />
                  <button style={{
                    padding: '6px 12px',
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: 4,
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}>Subscribe</button>
                </div>
              </div>
            </div>

            {/* Social in column 4 */}
            <div style={{ gridColumn: 4, gridRow: 2, textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>Follow Us</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { name: 'LinkedIn', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                  { name: 'Twitter', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg> },
                  { name: 'YouTube', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> }
                ].map(social => (
                  <a key={social.name} href="#" style={{ color: '#64748b', textDecoration: 'none' }} title={social.name}>
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Platform capability indicators — factual, not marketing metrics.
              Previous block contained unverifiable/aspirational claims (500+ customers,
              99.9% SLA, 24/7 support, platform-level certifications) that have no basis
              in current company facts. Replaced with six capability statements that can
              be demonstrated on the live platform. */}
          <div className="trust-indicators" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, marginBottom: 48, padding: 32, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12 }}>
            {[
              { title: 'Full compliance lifecycle', body: 'Define → Assign → Monitor → Audit → Certify → Improve, in one connected system of record.' },
              { title: 'Persona-aware AI assistants', body: 'Six role-scoped AI agents that read live compliance data and never write without confirmation.' },
              { title: 'Multi-framework mapping', body: 'Author once, map across every framework your organisation runs.' },
              { title: 'Audit-grade trail', body: 'Every evidence item, decision, and override is logged against the framework it satisfies.' },
              { title: 'Integrated ESG', body: 'Scope 1/2/3 emissions sit in the same audit workflow as your compliance evidence.' },
              { title: 'UK-hosted, UK GDPR-aligned', body: 'Data stays in the UK; built around UK GDPR, PECR, and ICO guidance from the ground up.' },
            ].map(stat => (
              <div key={stat.title} style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#ffffff', marginBottom: 6 }}>{stat.title}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>{stat.body}</div>
              </div>
            ))}
          </div>

          {/* Bottom bar — EIS-Tech is the product; Yiontech Ltd is the
              developer. Attribution follows "Powered by" convention. */}
          <div className="footer-bottom" style={{ borderTop: `1px solid rgba(255,255,255,0.08)`, paddingTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 4 }}>© {new Date().getFullYear()} EIS-Tech. All rights reserved.</div>
              <div style={{ color: '#64748b', fontSize: 11 }}>
                Powered by Yiontech Ltd &middot; Registered in England &amp; Wales
              </div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                <a href="mailto:support@eis-tech.co.uk" style={{ color: '#cbd5e1', textDecoration: 'none' }}>support@eis-tech.co.uk</a>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', textAlign: 'left' }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>Legal</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Privacy Policy', slug: 'privacy-policy' },
                    { label: 'Terms of Service', slug: 'terms-of-service' },
                    { label: 'Cookie Policy', slug: 'cookie-policy' },
                    { label: 'GDPR Compliance', slug: 'gdpr-compliance' },
                    { label: 'Acceptable Use', slug: 'acceptable-use-policy' },
                    { label: 'Refund Policy', slug: 'refund-policy' },
                  ].map(link => (
                    <Link key={link.slug} to={`/page/${link.slug}`} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 12 }}>{link.label}</Link>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>Policies</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Data Protection', slug: 'data-protection-policy' },
                    { label: 'Information Security', slug: 'information-security-policy' },
                    { label: 'Modern Slavery Statement', slug: 'modern-slavery-statement' },
                    { label: 'Anti-Bribery Policy', slug: 'anti-bribery-policy' },
                    { label: 'Environmental Policy', slug: 'environmental-policy' },
                  ].map(link => (
                    <Link key={link.slug} to={`/page/${link.slug}`} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 12 }}>{link.label}</Link>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>More</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Accessibility', slug: 'accessibility-statement' },
                    { label: 'Equality & Diversity', slug: 'equality-diversity-policy' },
                    { label: 'Health & Safety', slug: 'health-safety-policy' },
                    { label: 'Whistleblowing', slug: 'whistleblowing-policy' },
                    { label: 'Contact Information', slug: 'contact-information' },
                  ].map(link => (
                    <Link key={link.slug} to={`/page/${link.slug}`} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: 12 }}>{link.label}</Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Final Compliance Notice */}
          <div style={{ marginTop: 24, padding: 16, background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.1)', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>🔒 Enterprise-Grade Security & Compliance</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              Your data is protected by bank-level encryption, regular security audits, and compliance with the highest international standards.
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        /* ── Mobile responsive ─────────────────────────────────────── */

        /* Nav */
        .nav-links { display: flex; }
        .nav-actions { display: flex; }
        .nav-mobile { display: none !important; }

        /* Feature tab bar scrollable on mobile */
        .tab-bar { display: flex; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
        .tab-bar::-webkit-scrollbar { display: none; }

        @media (max-width: 860px) {
          /* Nav */
          .nav-links { display: none !important; }
          .nav-actions { display: none !important; }
          .nav-mobile { display: flex !important; }

          /* Section padding */
          .hero-section { padding-top: 160px !important; padding-bottom: 60px !important; }

          /* Hero */
          .hero-grid { grid-template-columns: 1fr !important; gap: 36px !important; }

          /* AI panel text column */
          .ai-text { padding: 40px 28px !important; }

          /* Lifecycle */
          .lifecycle-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 20px !important; }

          /* Features */
          .feature-grid { grid-template-columns: 1fr !important; gap: 40px !important; }

          /* AI panel */
          .ai-panel { grid-template-columns: 1fr !important; }
          .ai-chat { display: none !important; }

          /* Expert section */
          .expert-grid { grid-template-columns: 1fr !important; gap: 40px !important; }

          /* Security */
          .security-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Multi-framework */
          .fw-grid { grid-template-columns: 1fr !important; gap: 40px !important; }

          /* Trust portal */
          .trust-grid { grid-template-columns: 1fr !important; gap: 40px !important; }

          /* ESG */
          .esg-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Stats */
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .stats-grid > div { border-right: none !important; padding: 28px 16px !important; }

          /* Rating badges */
          .rating-badges { flex-wrap: wrap !important; justify-content: center !important; }

          /* Testimonials */
          .testimonials-grid { grid-template-columns: 1fr !important; }

          /* Footer */
          .footer-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .footer-grid > div:first-child { grid-column: span 1; }

          /* Trust indicators */
          .trust-indicators { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }

          /* Footer bottom */
          .footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .footer-bottom > div { flex-wrap: wrap; gap: 16px !important; }

          /* CTA */
          .cta-inner { padding: 48px 28px !important; border-radius: 20px !important; }
          .cta-buttons { flex-direction: column !important; align-items: stretch !important; max-width: 360px; margin: 0 auto 28px !important; }
          .cta-buttons a { text-align: center !important; padding: 14px 24px !important; }
        }

        @media (max-width: 540px) {
          /* Lifecycle — 2 cols on small screens */
          .lifecycle-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* ESG — 1 col on small screens */
          .esg-grid { grid-template-columns: 1fr !important; }

          /* Stats — 2 cols (keep) */
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Footer — single col */
          .footer-grid { grid-template-columns: 1fr !important; }
          .footer-grid > div:first-child { grid-column: span 1; }
        }

        /* AI Widget Animations */
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>

      {/* Cookie Consent Banner */}
      {!cookieConsent && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 444444,
          backgroundColor: 'rgba(11, 33, 42, 1)', backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${C.border}`, padding: '20px 28px',
        }}>
          <div style={{ maxWidth: 1220, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <p style={{ color: C.gray2, fontSize: 13, margin: 0, lineHeight: 1.6, maxWidth: 640 }}>
              We use cookies to improve your experience and analyse site usage. You can manage your preferences at any time.
              <Link to="/page/cookie-policy" style={{ color: C.lime, textDecoration: 'none', marginLeft: 4 }}>Cookie Policy</Link>
            </p>
            <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
              <button onClick={() => { localStorage.setItem('cookie_consent', 'rejected'); setCookieConsent('rejected'); }} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.gray2, borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Reject all</button>
              <button onClick={() => { localStorage.setItem('cookie_consent', 'manage'); setCookieConsent('manage'); }} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.gray2, borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Manage preferences</button>
              <button onClick={() => { localStorage.setItem('cookie_consent', 'accepted'); setCookieConsent('accepted'); }} style={{ backgroundColor: C.lime, color: '#000', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Accept all</button>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Chatbot */}
      <VisitorChatbot designTokens={C} />

    </div>
  );
}
